import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { requestGetStudents,
  getStudentsNotEvaluatedThisMonth, auth } from "../utils/resource";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faHouse,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import "../styles/StudentSelector.css";

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;
  return (
    <div className="s-s-pagination">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        Anterior
      </button>
      <span>
        {currentPage} de {totalPages}
      </span>
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        Próximo
      </button>
    </div>
  );
};

const StudentSelector = () => {
  const navigate = useNavigate();

  const [allStudents, setAllStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLevel, setSelectedLevel] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const levels = ["Básico", "Intermediário", "Avançado"];

  const fetchStudents = useCallback(async () => {
    setIsLoading(true);
    const result =
      filterType === "all"
        ? await requestGetStudents()
        : await getStudentsNotEvaluatedThisMonth();

    if (result.status === "success") {
      setAllStudents(result.data);
    } else {
      console.error("Falha ao buscar alunos:", result.message);
      setAllStudents([]);
    }
    setIsLoading(false);
    setCurrentPage(1);
  }, [filterType]);

  useEffect(() => {
    if (!auth?.currentUser?.email) {
      navigate("/");
    }
    fetchStudents();
  }, [navigate, fetchStudents]);

  const { displayedStudents, totalPages } = useMemo(() => {
    const processedStudents = allStudents
      .filter((student) =>
        student.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => a.name.localeCompare(b.name));

    const total = Math.ceil(processedStudents.length / itemsPerPage);

    const paginated = processedStudents.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    );

    return { displayedStudents: paginated, totalPages: total };
  }, [allStudents, searchTerm, currentPage, itemsPerPage]);

  const handleStartEvaluation = (student) => {
    if (student && selectedLevel) {
      navigate("/student-evaluation", {
        state: { studentId: student.id, level: selectedLevel },
      });
    } else {
      alert("Por favor, selecione um nível antes de avaliar.");
    }
  };

  const handleCancel = () => navigate("/dashboard");

  return (
    <div className="s-s-page-container">
      <div className="s-s-top-nav-bar">
        <FontAwesomeIcon
          icon={faHouse}
          className="icon cancel-icon"
          onClick={handleCancel}
          title="Voltar ao Dashboard"
        />
      </div>

      <div className="s-s-content-card">
        <h2>Avaliar Aluno</h2>

        <div className="s-s-form-group">
          <label>1. Selecione o Nível</label>
          <div className="s-s-level-buttons">
            {levels.map((level) => (
              <button
                key={level}
                className={`s-s-level-btn ${
                  selectedLevel === level ? "active" : ""
                }`}
                onClick={() => setSelectedLevel(level)}
              >
                {level}
              </button>
            ))}
          </div>
        </div>

        <div className="s-s-form-group">
          <label>2. Encontre o Aluno</label>
          <div className="s-s-filter-controls">
            <div className="s-s-filter-buttons">
              <button
                className={filterType === "all" ? "active" : ""}
                onClick={() => setFilterType("all")}
              >
                Todos
              </button>
              <button
                className={filterType === "unevaluated" ? "active" : ""}
                onClick={() => setFilterType("unevaluated")}
              >
                Não Avaliados
              </button>
            </div>
            <div className="s-s-search-wrapper">
              <FontAwesomeIcon icon={faSearch} className="s-s-search-icon" />
              <input
                type="text"
                className="s-s-search-input"
                placeholder="Pesquisar por nome..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>
        </div>

        <div className="s-s-student-list">
          {isLoading ? (
            <p className="s-s-loading-text">Carregando alunos...</p>
          ) : displayedStudents.length > 0 ? (
            displayedStudents.map((student) => (
              <div key={student.id} className="s-s-student-item">
                <span className="s-s-student-name">{student.name}</span>
                <button
                  className="s-s-list-eval-btn"
                  onClick={() => handleStartEvaluation(student)}
                  disabled={!selectedLevel}
                  title={!selectedLevel ? "Selecione um nível primeiro" : ""}
                >
                  Avaliar
                  <FontAwesomeIcon icon={faArrowRight} />
                </button>
              </div>
            ))
          ) : (
            <p className="s-s-loading-text">Nenhum aluno encontrado.</p>
          )}
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>
    </div>
  );
};

export default StudentSelector;
